import { useEffect, useState, useCallback, useRef } from "react";
import { CollapsibleTenantSelector } from "../components/CollapsibleTenantSelector";
import { CollapsibleCloudSelector } from "../components/CollapsibleCloudSelector";
import { CollapsibleSearchBar } from "../components/CollapsibleSearchBar";
import { LogoutButton } from "../components/LogoutButton";
import { ThemeToggle } from "../components/ThemeToggle";
import { ReactFlowGraph } from "../components/graph/ReactFlowGraph";
import { GraphModeSelector } from "../components/GraphModeSelector";
import { CollapsibleInspector } from "../components/CollapsibleInspector";
import { CollapsibleViewFilter, ViewFilterType } from "../components/CollapsibleViewFilter";
import { CollapsibleControlBar } from "../components/CollapsibleControlBar";
import { LoadingPage, LoadingStep } from "../components/LoadingPage";
import { SwitchingOverlay } from "../components/SwitchingOverlay";
import { useGraphStore } from "../store/graphStore";
import { fetchGraphData, transformApiDataToGraph } from "../services/graphApi";
import {
  fetchTenants,
  fetchGcpProjectIds,
  fetchAwsAccounts,
  fetchAzureSubscriptions,
  fetchCorrelations,
  fetchBlastRadius,
} from "../services/cloudApi";
import { transformCorrelationsToGraph, transformBlastRadiusToGraph } from "../services/correlationGraphApi";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import type { CloudType } from "../types/api";
import type { GraphNode } from "../types/graph";
import { UICollapseProvider } from "../hooks/useUICollapse";
import {
  readDashboardGraphQuickLink,
  type DashboardGraphQuickLinkPayload,
} from "../../CloudDashboard/lib/cloudEntityQuickLinks";
import { setLocalStorageTenant } from "../../../../services/Helpers";

const MAX_LOADING_TIME = 10000; // 10 seconds max for graph data

interface Tenant {
  tenantName: string;
  displayName: string;
  [key: string]: any;
}

const lsSetValue = (key: string, value: any) => {
  localStorage.setItem(key, JSON.stringify({ value }));
};

export interface AwsAccountInfo {
  accountId: string;
  accountName: string;
}

export interface AzureSubscriptionInfo {
  subscriptionId: string;
  subscriptionName: string;
}

interface CloudAccounts {
  gcp: string[];
  aws: AwsAccountInfo[];
  azure: AzureSubscriptionInfo[];
}

const normalizeQuickLinkValue = (value: any) =>
  String(value || "").trim().toLowerCase();

const getQuickLinkRootPrefix = (
  targetNodeType: DashboardGraphQuickLinkPayload["targetNodeType"],
) => {
  if (targetNodeType === "user") return "user-";
  if (targetNodeType === "service_account") return "sa-";
  return "resource-center-";
};

const isQuickLinkRootNode = (
  node: GraphNode,
  targetNodeType: DashboardGraphQuickLinkPayload["targetNodeType"],
) => {
  const expectedPrefix = getQuickLinkRootPrefix(targetNodeType);

  return (
    node.type === targetNodeType &&
    node.props?.graphId?.startsWith(expectedPrefix)
  );
};

const getQuickLinkNodeValues = (node: GraphNode) =>
  [
    node.id,
    node.label,
    node.props?.graphId,
    node.props?.email,
    node.props?.serviceAccountEmail,
    node.props?.roleArn,
    node.props?.roleName,
    node.props?.principalId,
    node.props?.userPrincipalName,
    node.props?.mail,
    node.props?.displayName,
    node.props?.name,
    node.props?.instanceId,
    node.props?.azureId,
    node.props?.azureVmId,
    node.props?.storageAccountName,
    node.props?.databaseName,
    node.props?.resourceName,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

const findQuickLinkTargetNode = (
  nodes: GraphNode[],
  payload: DashboardGraphQuickLinkPayload,
): GraphNode | null => {
  const preferredNodeIds = new Set(
    payload.preferredNodeIds.map(normalizeQuickLinkValue).filter(Boolean),
  );
  const preferredGraphIds = new Set(
    payload.preferredGraphIds.map(normalizeQuickLinkValue).filter(Boolean),
  );
  const candidateValues = new Set(
    payload.candidateValues.map(normalizeQuickLinkValue).filter(Boolean),
  );
  const normalizedTargetLabel = normalizeQuickLinkValue(payload.targetLabel);

  let bestMatch: GraphNode | null = null;
  let bestScore = -1;

  nodes.forEach((node) => {
    if (!isQuickLinkRootNode(node, payload.targetNodeType)) {
      return;
    }

    let score = 0;
    const normalizedNodeId = normalizeQuickLinkValue(node.id);
    const normalizedGraphId = normalizeQuickLinkValue(node.props?.graphId);

    if (preferredNodeIds.has(normalizedNodeId)) {
      score += 120;
    }

    if (preferredGraphIds.has(normalizedGraphId)) {
      score += 100;
    }

    getQuickLinkNodeValues(node).forEach((value) => {
      if (candidateValues.has(normalizeQuickLinkValue(value))) {
        score += 25;
      }
    });

    if (normalizeQuickLinkValue(node.label) === normalizedTargetLabel) {
      score += 15;
    }

    if (
      payload.activeConnectionType === "sa" &&
      payload.activeConnectionSubFilter === "user-managed" &&
      node.props?.isUserManaged === true
    ) {
      score += 5;
    }

    if (
      payload.activeConnectionType === "sa" &&
      payload.activeConnectionSubFilter === "cloud-managed" &&
      node.props?.isUserManaged === false
    ) {
      score += 5;
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = node;
    }
  });

  return bestScore > 0 ? bestMatch : null;
};

const resolveQuickLinkSubFilter = (
  payload: DashboardGraphQuickLinkPayload,
  targetNode: GraphNode | null,
) => {
  if (payload.activeConnectionType !== "sa") {
    return payload.activeConnectionSubFilter || undefined;
  }

  if (targetNode?.props?.isUserManaged === true) {
    return "user-managed";
  }

  if (targetNode?.props?.isUserManaged === false) {
    return "cloud-managed";
  }

  return payload.activeConnectionSubFilter || undefined;
};

const applyGraphQuickLinkSelection = (
  payload: DashboardGraphQuickLinkPayload,
) => {
  const store = useGraphStore.getState();
  const targetNode = findQuickLinkTargetNode(store.allNodes, payload);
  const resolvedSubFilter = resolveQuickLinkSubFilter(payload, targetNode);

  store.setActiveConnectionType(
    payload.activeConnectionType,
    resolvedSubFilter,
  );

  const searchQuery =
    payload.preferredSearchQuery ||
    targetNode?.props?.serviceAccountEmail ||
    targetNode?.props?.email ||
    targetNode?.label ||
    payload.targetLabel;

  if (searchQuery) {
    store.setSearchQuery(searchQuery);
  }

  if (targetNode) {
    store.navigateToNode(targetNode.id);
    return true;
  }

  return false;
};

const Index = () => {
  const [searchParams] = useSearchParams();
  const graphLinkId = searchParams.get("graphLinkId") || "";
  const graphQuickLinkPayload = readDashboardGraphQuickLink(graphLinkId);
  const graphQuickLinkTenantName = graphQuickLinkPayload?.tenantName || "";
  const expiredQuickLinkNoticeRef = useRef(false);
  const quickLinkLoadStartedRef = useRef(false);
  const quickLinkFocusHandledRef = useRef(false);
  const {
    setGraphData,
    setModeGraphData,
    theme,
    viewFilter,
    setViewFilter,
    viewFilterOpen,
    isModeLoading,
    setActiveMode,
    setModeLoading,
    activeMode,
    setCorrelationsData,
    setBlastRadiusData,
    correlationsData,
    blastRadiusData,
    getActivityAllNodes,
  } = useGraphStore();

  // Phase management
  const [phase, setPhase] = useState<"selection" | "loading" | "ready">(
    graphQuickLinkPayload ? "loading" : "selection",
  );

  // Selection phase state
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [cloudAccounts, setCloudAccounts] = useState<CloudAccounts>({ gcp: [], aws: [], azure: [] });
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState<{
    tenantName: string;
    cloudType: CloudType;
    cloudId: string;
  } | null>(null);

  // Loading phase state
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingCloud, setSwitchingCloud] = useState<{ cloudType: string; cloudId: string } | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingSteps, setLoadingSteps] = useState<LoadingStep[]>([
    { id: "users", label: "User Nodes", status: "pending" },
    { id: "sa", label: "Service Accounts", status: "pending" },
    { id: "resources", label: "Resources", status: "pending" },
    { id: "roles", label: "Roles & Policies", status: "pending" },
    { id: "edges", label: "Connections", status: "pending" },
  ]);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dataReceivedRef = useRef(false);

  // Handle theme changes
  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  // Fetch tenants and cloud accounts on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoadingAccounts(true);

      try {
        // Fetch tenants first
        const tenantsData = await fetchTenants();
        const formattedTenants: Tenant[] = tenantsData.map((t) => ({
          ...t,
          tenantName: t.tenantName,
          displayName: t.displayName || t.tenantName,
        }));
        setTenants(formattedTenants);

        // If a graph quick link opened this page, bootstrap the same tenant immediately.
        if (formattedTenants.length > 0) {
          const tenantName =
            graphQuickLinkTenantName || formattedTenants[0].tenantName;
          const tenant = tenantsData.find((item) => item.tenantName === tenantName);

          if (tenant !== undefined) {
            setLocalStorageTenant(tenant);
          }

          // Fetch all cloud accounts in parallel
          const [gcpRes, awsRes, azureRes] = await Promise.allSettled([
            fetchGcpProjectIds(tenantName),
            fetchAwsAccounts(tenantName),
            fetchAzureSubscriptions(tenantName),
          ]);

          const gcpIds = gcpRes.status === "fulfilled" ? gcpRes.value : [];
          const awsAccounts =
            awsRes.status === "fulfilled"
              ? awsRes.value.map((a) => ({ accountId: a.accountId, accountName: a.accountName || a.accountId }))
              : [];
          const azureSubscriptions =
            azureRes.status === "fulfilled"
              ? azureRes.value
                .filter((a) => a.azureSubscriptionId)
                .map((a) => ({
                  subscriptionId: a.azureSubscriptionId,
                  subscriptionName: a.subscriptionName || a.azureSubscriptionId,
                }))
              : [];

          setCloudAccounts({
            gcp: gcpIds,
            aws: awsAccounts,
            azure: azureSubscriptions,
          });
        }
      } catch (error) {
        console.error("Failed to load initial data:", error);
        toast.error("Failed to load tenants and cloud accounts");
      } finally {
        setIsLoadingAccounts(false);
      }
    };

    loadInitialData();
  }, [graphQuickLinkTenantName]);

  useEffect(() => {
    if (!graphLinkId || graphQuickLinkPayload || expiredQuickLinkNoticeRef.current) {
      return;
    }

    expiredQuickLinkNoticeRef.current = true;
    toast.error(
      "Cloud graph link expired. Please reopen the entity from the NHI Dashboard.",
    );
  }, [graphLinkId, graphQuickLinkPayload]);

  // Load graph data function with step tracking
  const loadGraphData = useCallback(
    async (tenantName: string, cloudType: CloudType, cloudId: string) => {
      // Reset steps
      setLoadingSteps([
        { id: "users", label: "User Nodes", status: "pending" },
        { id: "sa", label: "Service Accounts", status: "pending" },
        { id: "resources", label: "Resources", status: "pending" },
        { id: "roles", label: "Roles & Policies", status: "pending" },
        { id: "edges", label: "Connections", status: "pending" },
      ]);
      setLoadingProgress(0);
      dataReceivedRef.current = false;

      // Start all steps as loading
      setLoadingSteps((prev) => prev.map((s) => ({ ...s, status: "loading" as const })));

      try {
        console.log("Fetching data from APIs...", { tenantName, cloudType, cloudId });

        // Fetch graph data AND correlations/blast radius in parallel
        const [apiData, corrData, blastData] = await Promise.all([
          fetchGraphData({ tenantName, cloudId, cloudType }),
          fetchCorrelations(tenantName, cloudId),
          fetchBlastRadius(tenantName, cloudId),
        ]);

        console.log("API data received, transforming...", apiData);
        console.log("Correlations data:", corrData?.length || 0, "items");
        console.log("Blast radius data:", blastData?.length || 0, "items");

        // Store correlations and blast radius raw data
        setCorrelationsData(corrData || []);
        setBlastRadiusData(blastData || []);

        // Mark steps as complete (progress percentage is handled by staged animation in handleStartLoading)
        setLoadingSteps((prev) => prev.map((s) => ({ ...s, status: "complete" as const })));

        // Transform with try-catch to handle any transformation errors
        let graphData;
        try {
          graphData = transformApiDataToGraph(apiData, { tenantName, cloudType, cloudId });
          console.log("Graph data transformed:", graphData);
        } catch (transformError) {
          console.error("Error transforming graph data:", transformError);
          graphData = { nodes: [], edges: [], stats: { users: 0, service_accounts: 0, resources: 0, edges: 0 } };
        }

        dataReceivedRef.current = true;
        // Pass queryResults to the store
        setGraphData(graphData.nodes, graphData.edges, apiData.queryResults);
        toast.success("Graph data loaded successfully");
        return true;
      } catch (err) {
        console.error("Error loading graph data:", err);

        // Mark failed steps
        setLoadingSteps((prev) => prev.map((s) => (s.status === "loading" ? { ...s, status: "error" as const } : s)));

        toast.error("Failed to load graph data. Please check your connection and token.");
        dataReceivedRef.current = true;
        setGraphData([], []);
        return false;
      }
    },
    [setGraphData, setCorrelationsData, setBlastRadiusData],
  );

  // Handle start loading from selection phase
  const handleStartLoading = useCallback(
    async (tenantName: string, cloudType: CloudType, cloudId: string) => {
      setSelectedConfig({ tenantName, cloudType, cloudId });
      setActiveMode("activity");
      setPhase("loading");
      const tenant = tenants.find((item) => item.tenantName === tenantName);

      if (tenant !== undefined) {
        setLocalStorageTenant(tenant);
      }

      // Clear all cloud selections first, then set the selected one
      // This ensures CloudSelector picks the correct one
      lsSetValue("gcpSelectedProjectId", "");
      lsSetValue("awsSelectedAccountId", "");
      lsSetValue("azureSelectedSubscriptionId", "");

      if (cloudType === "GCP") {
        lsSetValue("gcpSelectedProjectId", cloudId);
      } else if (cloudType === "AWS") {
        lsSetValue("awsSelectedAccountId", cloudId);
      } else if (cloudType === "AZURE") {
        lsSetValue("azureSelectedSubscriptionId", cloudId);
      }

      // Set up timeout
      loadingTimeoutRef.current = setTimeout(() => {
        if (!dataReceivedRef.current) {
          console.log("Loading timeout reached, proceeding with available data");
          toast.warning("Loading timeout - displaying available data");
          setPhase("ready");
        }
      }, MAX_LOADING_TIME);

      // Run staged progress animation alongside API call
      const stageDelays = [
        { progress: 10, delay: 400 + Math.random() * 300 },
        { progress: 25, delay: 500 + Math.random() * 400 },
        { progress: 62, delay: 600 + Math.random() * 500 },
      ];

      const animateProgress = async () => {
        for (const stage of stageDelays) {
          await new Promise((r) => setTimeout(r, stage.delay));
          setLoadingProgress(stage.progress);
        }
      };

      const minDuration = new Promise((r) => setTimeout(r, 2000));

      await Promise.all([
        loadGraphData(tenantName, cloudType, cloudId),
        animateProgress(),
        minDuration,
      ]);

      if (graphQuickLinkPayload) {
        const didFocusQuickLink = applyGraphQuickLinkSelection(
          graphQuickLinkPayload,
        );

        if (didFocusQuickLink) {
          quickLinkFocusHandledRef.current = true;
        }
      }

      // After both API and animation complete, jump to 100%
      setLoadingProgress(100);
      setLoadingSteps((prev) => prev.map((s) => ({ ...s, status: "complete" as const })));

      // Brief pause at 100% before transitioning
      await new Promise((r) => setTimeout(r, 400));

      // Clear timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }

      setPhase("ready");
    },
    [graphQuickLinkPayload, loadGraphData, setActiveMode],
  );

  useEffect(() => {
    if (
      !graphQuickLinkPayload ||
      quickLinkLoadStartedRef.current ||
      phase === "ready"
    ) {
      return;
    }

    quickLinkLoadStartedRef.current = true;
    handleStartLoading(
      graphQuickLinkPayload.tenantName,
      graphQuickLinkPayload.cloudType,
      graphQuickLinkPayload.cloudId,
    );
  }, [
    graphQuickLinkPayload,
    phase,
    handleStartLoading,
  ]);

  useEffect(() => {
    if (
      !graphQuickLinkPayload ||
      quickLinkFocusHandledRef.current ||
      phase !== "ready" ||
      activeMode !== "activity"
    ) {
      return;
    }

    quickLinkFocusHandledRef.current = true;

    let firstFrame = 0;
    let secondFrame = 0;

    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        const didFocusQuickLink = applyGraphQuickLinkSelection(
          graphQuickLinkPayload,
        );

        if (didFocusQuickLink) {
          quickLinkFocusHandledRef.current = true;
          return;
        }

        toast.warning(
          `Could not find ${graphQuickLinkPayload.targetLabel || "this entity"} in the activity graph. The graph opened in the closest matching view instead.`,
        );
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [
    activeMode,
    graphQuickLinkPayload,
    phase,
  ]);

  // Handle mode change from GraphModeSelector
  const handleModeChange = useCallback(
    async (mode: "activity" | "correlations" | "blast-radius") => {
      setActiveMode(mode);

      if (mode === "activity" && selectedConfig) {
        setModeLoading(true);
        await loadGraphData(selectedConfig.tenantName, selectedConfig.cloudType, selectedConfig.cloudId);
        setModeLoading(false);
      } else if (mode === "correlations") {
        const activityNodes = getActivityAllNodes();
        const graphData = transformCorrelationsToGraph(correlationsData, activityNodes);
        console.log("Correlations graph:", graphData.nodes.length, "nodes,", graphData.edges.length, "edges");
        setModeGraphData(graphData.nodes, graphData.edges);
      } else if (mode === "blast-radius") {
        const activityNodes = getActivityAllNodes();
        const graphData = transformBlastRadiusToGraph(blastRadiusData, activityNodes);
        console.log("Blast radius graph:", graphData.nodes.length, "nodes,", graphData.edges.length, "edges");
        setModeGraphData(graphData.nodes, graphData.edges);
      }
    },
    [
      selectedConfig,
      loadGraphData,
      setActiveMode,
      setModeLoading,
      setModeGraphData,
      correlationsData,
      blastRadiusData,
      getActivityAllNodes,
    ],
  );

  // Handle cloud account change (from main page)
  const handleCloudChange = useCallback(
    async (cloudType: CloudType, cloudId: string) => {
      if (!selectedConfig) return;

      setSwitchingCloud({ cloudType, cloudId });
      setIsSwitching(true);

      // Update localStorage BEFORE loading data to ensure CloudSelector stays in sync
      lsSetValue("gcpSelectedProjectId", "");
      lsSetValue("awsSelectedAccountId", "");
      lsSetValue("azureSelectedSubscriptionId", "");

      if (cloudType === "GCP") {
        lsSetValue("gcpSelectedProjectId", cloudId);
      } else if (cloudType === "AWS") {
        lsSetValue("awsSelectedAccountId", cloudId);
      } else if (cloudType === "AZURE") {
        lsSetValue("azureSelectedSubscriptionId", cloudId);
      }

      // Dispatch event to sync CloudSelector immediately
      window.dispatchEvent(new CustomEvent("cloud-selection-changed"));

      try {
        await loadGraphData(selectedConfig.tenantName, cloudType, cloudId);
        setSelectedConfig((prev) => (prev ? { ...prev, cloudType, cloudId } : null));
      } finally {
        setIsSwitching(false);
        setSwitchingCloud(null);
      }
    },
    [loadGraphData, selectedConfig],
  );

  // Selection phase
  if (phase === "selection") {
    return (
      <LoadingPage
        phase="selection"
        tenants={tenants}
        cloudAccounts={cloudAccounts}
        isLoadingAccounts={isLoadingAccounts}
        onStartLoading={handleStartLoading}
      />
    );
  }

  // Loading phase
  if (phase === "loading") {
    return <LoadingPage phase="loading" steps={loadingSteps} progress={loadingProgress} />;
  }

  // Ready phase - main app
  return (
    <UICollapseProvider>
      <div className="relative flex flex-col h-screen w-full overflow-hidden bg-graph-bg">
        {/* Switching Overlay */}
        {isSwitching && switchingCloud && (
          <SwitchingOverlay cloudType={switchingCloud.cloudType} cloudId={switchingCloud.cloudId} />
        )}

        {/* Static Grid Background */}
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--border)/0.08) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)/0.08) 1px, transparent 1px)`,
            backgroundSize: "48px 48px",
          }}
        />

        {/* Desktop - Top Left - Logout Button */}
        <div className="hidden lg:block absolute top-6 left-6 z-50 animate-slide-down-slow">
          <LogoutButton
            onLogout={() => {
              setActiveMode("activity");
              setPhase("selection");
            }}
          />
        </div>

        <div
          className="hidden xl:flex absolute top-24 left-6 z-50 flex-col gap-3 animate-slide-in-left-slow"
          style={{ animationDelay: "0.2s" }}
        >
          <CollapsibleTenantSelector />
          <CollapsibleCloudSelector onCloudChange={handleCloudChange} />
          {viewFilterOpen && (
            <CollapsibleViewFilter
              selected={viewFilter as ViewFilterType}
              onChange={(filter) => setViewFilter(filter)}
            />
          )}
        </div>

        {/* Search Bar - Top center */}
        <div
          className="hidden md:flex absolute top-4 lg:top-4 left-0 right-0 z-[70] animate-slide-down-slow justify-center px-4 pointer-events-none"
          style={{ animationDelay: "0.1s" }}
        >
          <div className="pointer-events-auto">
            <CollapsibleSearchBar />
          </div>
        </div>

        {/* Mode Selector - Below search bar */}
        <div
          className="hidden md:flex absolute top-[90px] lg:top-[90px] left-0 right-0 z-40 justify-center px-4 animate-slide-down-slow pointer-events-none"
          style={{ animationDelay: "0.15s" }}
        >
          <div className="pointer-events-auto">
            <GraphModeSelector onModeChange={handleModeChange} />
          </div>
        </div>

        {/* Desktop - Top Right - Theme Toggle */}
        <div
          className="hidden lg:block absolute top-6 right-6 z-50 animate-slide-down-slow"
          style={{ animationDelay: "0.15s" }}
        >
          <ThemeToggle />
        </div>

        {/* Main Content Area */}
        <div className="flex flex-1 relative overflow-hidden z-10 pt-16 lg:pt-0">
          {/* Graph Canvas - Full width on mobile, adjusted on desktop */}
          <div className="flex-1 relative z-0 w-full">
            <ReactFlowGraph />

            {/* Mode Loading Overlay */}
            {isModeLoading && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/60 backdrop-blur-sm">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">
                    Loading{" "}
                    {activeMode === "correlations"
                      ? "Correlations"
                      : activeMode === "blast-radius"
                        ? "Blast Radius"
                        : "Activity"}
                    …
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Inspector Panel - Collapsible */}
          <div
            className="hidden lg:flex flex-col items-start gap-4 absolute top-24 right-6 bottom-24 xl:bottom-28 z-40 min-h-0 animate-slide-in-right-slow"
            style={{ animationDelay: "0.4s" }}
          >
            <CollapsibleInspector />
          </div>
        </div>

        {/* Bottom Control Bar - Responsive */}
        <div className="relative z-50 animate-slide-up-from-bottom" style={{ animationDelay: "0.3s" }}>
          <CollapsibleControlBar />
        </div>
      </div>
    </UICollapseProvider>
  );
};

export default Index;
